import { recipe } from "@vanilla-extract/recipes";
import { style } from "@vanilla-extract/css";
import { rem } from "@huub-web/utils/css/rem";

import { activeState } from "../../theme/activeState";
import { transition } from "../../utils/transition";
import {
  responsiveContainer,
  responsive,
  responsiveCard,
} from "../../utils/responsive";
import { disabledState } from "../../theme/disabledState";

import { marqueeStyle } from "../Marquee/Marquee.css";
import { theme } from "../../theme/css/theme.css";
import { radioCardStyle } from "../RadioCards/RadioCards.css";
import { carouselStyle } from "../Carousel/Carousel.css";

const CARD_RESPONSIVE_HORIZONTAL_BREAKPOINT = 640;

export const cardStyle = style({
  // Keep
});

export const cardContainerStyle = recipe({
  base: {
    position: "relative",
    gap: theme.spacings.s,
    zIndex: 2,
    transition: transition(
      "transform",
      "box-shadow",
      "opacity",
      "border-radius",
      "padding",
    ),
    display: "block",
    width: "100%",
    textAlign: "left",
    scrollSnapAlign: "center",
    containerType: "inline-size",
    containerName: "card",
    selectors: {
      [`${marqueeStyle} &`]: {
        width: rem(320),
      },
      [`${carouselStyle} &`]: {
        width: `var(--carousel-child-width, ${rem(300)})`,
        maxWidth: "var(--carousel-child-max-width)",
      },
      [`[data-wrapper="carousel"] ${radioCardStyle}.&`]: {
        width: rem(200),
        flexShrink: 0,
      },
      "&:focus-visible": {
        outline: "none",
      },
    },
  },
  variants: {
    variant: {
      opaque: {},
      primary: {},
      secondary: {},
      tertiary: {},
      accent: {},
      info: {},
      infoSurface: {},
      success: {},
      successSurface: {},
      warning: {},
      warningSurface: {},
      error: {},
      errorSurface: {},
      gradient: {},
      gradientAlternative: {},
      blur: {},
      navigation: {},
      childModel: {},
      panel: {},
    },
    /**
     * @deprecated
     */
    columns: {
      one: {
        gridColumn: "span 1",
      },
      two: {
        gridColumn: "span 2",
      },
    },
    disabled: {
      true: {
        ...disabledState,
      },
    },
    overflowHidden: {
      true: {
        overflow: "hidden",
      },
    },
  },
  defaultVariants: {
    variant: "primary",
    columns: "one",
  },
});

export const cardComponentStyle = recipe({
  base: {
    position: "relative",
    background: "var(--background)",
    zIndex: 2,
    minHeight: "100%",
    transition: transition(
      "border-color",
      "background-color",
      "outline-color",
      "box-shadow",
    ),
    borderRadius: `var(--border-radius, ${theme.borderRadiuses.s})`,
    outline: "1px solid transparent",
    ...responsiveCard({
      500: {
        vars: {
          "--border-radius": theme.borderRadiuses.m,
        },
      },
    }),
    selectors: {},
    vars: {},
  },
  variants: {
    hoverable: {
      true: {
        selectors: {
          [`${cardStyle}:focus-visible &`]: {
            outlineColor: theme.colors.outline,
            borderColor: theme.colors.outline,
          },
          ":not([disabled]) &:hover": {
            cursor: "pointer",
            borderColor: `var(--border-color__hover, ${theme.colors.primary})`,
            background: "var(--background__hover, var(--background))",
          },
          ":not([disabled]) &:active": {
            ...activeState,
          },
        },
      },
      false: {
        selectors: {},
      },
    },
    direction: {
      horizontal: {
        flexDirection: "row",
      },
      vertical: {
        flexDirection: "column",
      },
      responsiveHorizontal: {
        ...responsiveContainer({
          base: {
            flexDirection: "column",
          },
          [CARD_RESPONSIVE_HORIZONTAL_BREAKPOINT]: {
            flexDirection: "row",
          },
        }),
      },
    },
    variant: {
      opaque: {
        color: theme.colors.onBackground,
        vars: {
          "--background": "transparent",
          "--border-color__hover": `${theme.colors.onBackground} !important`,
        },
      },
      primary: {
        color: theme.colors.onBackground,
        vars: {
          "--background": theme.colors.background,
          "--border-color__hover": `${theme.colors.primary} !important`,
        },
      },
      secondary: {
        color: theme.colors.onSecondary,
        vars: {
          "--background": theme.colors.secondary,
        },
      },
      tertiary: {
        color: theme.colors.onTertiary,
        vars: {
          "--background": theme.colors.tertiary,
          "--background__hover": theme.colors.tertiary,
          "--border-color__hover": "transparent",
        },
      },
      accent: {
        color: theme.colors.onPrimary,
        vars: {
          "--background": theme.colors.primary,
          "--background__hover": theme.colors.primaryHover,
        },
      },
      info: {
        color: theme.colors.onInfo,
        vars: {
          "--background": theme.colors.info,
        },
      },
      infoSurface: {
        color: theme.colors.onInfoSurface,
        vars: {
          "--background": theme.colors.infoSurface,
        },
      },
      success: {
        color: theme.colors.onSuccess,
        vars: {
          "--background": theme.colors.success,
        },
      },
      successSurface: {
        color: theme.colors.onSuccessSurface,
        vars: {
          "--background": theme.colors.successSurface,
        },
      },
      warning: {
        color: theme.colors.onWarning,
        vars: {
          "--background": theme.colors.warning,
        },
      },
      error: {
        color: theme.colors.onError,
        vars: {
          "--background": theme.colors.error,
        },
      },
      errorSurface: {
        color: theme.colors.onErrorSurface,
        vars: {
          "--background": theme.colors.errorSurface,
        },
      },
      warningSurface: {
        color: theme.colors.onWarningSurface,
        vars: {
          "--background": theme.colors.warningSurface,
        },
      },
      navigation: {
        color: theme.colors.onBackground,
        vars: {
          "--background": theme.colors.navigation,
        },
      },
      gradient: {
        color: theme.colors.onPrimary,
        vars: {
          "--background": `linear-gradient(105deg, ${theme.colors.gradientA} -10%, ${theme.colors.gradientB} 25%, ${theme.colors.gradientC} 90%)`,
          "--border-color__hover": "transparent",
        },
      },
      gradientAlternative: {
        color: theme.colors.onPrimary,
        vars: {
          "--background": `linear-gradient(105deg, ${theme.colors.gradientC} -10%, ${theme.colors.gradientD} 90%)`,
          "--border-color__hover": "transparent",
        },
      },
      blur: {
        color: theme.colors.onBackground,
        backdropFilter: "blur(12px)",
        vars: {
          "--background": "rgba(255, 255, 255, 0.85)",
          "--background__hover": "rgba(255, 255, 255, 0.95)",
        },
      },
      childModel: {
        color: theme.colors.onBackground,
        border: `1px solid ${theme.colors.defaultBorder} !important`,
        vars: {
          "--background": theme.colors.backgroundTintMarketing,
          "--border-color__hover": `${theme.colors.primary} !important`,
        },
      },
      panel: {
        color: theme.colors.onBackground,
        vars: {
          "--background": theme.colors.panel,
        },
      },
    },
    overflowHidden: {
      true: {
        overflow: "hidden",
      },
    },
    padding: {
      none: {
        vars: {
          "--padding": "0",
        },
      },
      default: {
        vars: {
          "--padding": theme.spacings.s,
        },
        ...responsiveCard({
          400: {
            vars: {
              "--padding": theme.spacings.m,
            },
          },
          700: {
            vars: {
              "--padding": theme.spacings.l,
            },
          },
        }),
      },
      xs: {
        vars: {
          "--padding": theme.spacings.xs,
        },
      },
      s: {
        vars: {
          "--padding": theme.spacings.s,
        },
      },
      l: {
        vars: {
          "--padding": theme.spacings.s,
        },
        ...responsiveCard({
          300: {
            vars: {
              "--padding": theme.spacings.m,
            },
          },
          600: {
            vars: {
              "--padding": theme.spacings.l,
            },
          },
        }),
      },
      xl: {
        vars: {
          "--padding": theme.spacings.l,
        },
        ...responsiveCard({
          600: {
            vars: {
              "--padding": theme.spacings.xl,
            },
          },
        }),
      },
    },
    borderRadius: {
      none: {
        borderRadius: "0 !important",
        vars: {
          "--border-radius": "0 !important",
        },
      },
      xs: {
        borderRadius: `${theme.borderRadiuses.xs} !important`,
        vars: {
          "--border-radius": `${theme.borderRadiuses.xs} !important`,
        },
      },
      s: {
        borderRadius: `${theme.borderRadiuses.s} !important`,
        vars: {
          "--border-radius": `${theme.borderRadiuses.s} !important`,
        },
      },
      m: {
        borderRadius: `${theme.borderRadiuses.m} !important`,
        vars: {
          "--border-radius": `${theme.borderRadiuses.m} !important`,
        },
      },
      l: {
        borderRadius: `${theme.borderRadiuses.l} !important`,
        vars: {
          "--border-radius": `${theme.borderRadiuses.l} !important`,
        },
      },
    },
  },
  defaultVariants: {
    padding: "default",
    variant: "primary",
  },
});

export const cardContentStyle = recipe({
  base: {
    position: "relative",
    display: "flex",
    zIndex: 2,
    borderRadius: `var(--border-radius, ${theme.borderRadiuses.s})`,
    gap: "var(--gap)",
  },
  variants: {
    variant: {
      opaque: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      primary: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      secondary: {
        vars: {
          "--gap": theme.spacings.m,
        },
      },
      tertiary: {
        vars: {
          "--gap": theme.spacings.m,
        },
      },
      info: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      accent: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      infoSurface: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      successSurface: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      warningSurface: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      errorSurface: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      success: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      warning: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      error: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      gradient: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      gradientAlternative: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      blur: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      navigation: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      childModel: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      panel: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
    },
    applyBackground: {
      true: {
        background: "var(--background)",
      },
    },
    direction: {
      vertical: {
        flexDirection: "column",
      },
      horizontal: {
        flexDirection: "row",
        alignItems: "center",
      },
      responsiveHorizontal: {
        ...responsive({
          base: {
            flexDirection: "column",
          },
          s: {
            flexDirection: "row",
          },
        }),
      },
    },
    applyPadding: {
      true: {
        padding: "var(--padding)",
      },
    },
    gap: {
      none: {
        vars: {
          "--gap": "0",
        },
      },
      xs: {
        vars: {
          "--gap": theme.spacings.xs,
        },
      },
      s: {
        vars: {
          "--gap": theme.spacings.s,
        },
      },
      m: {
        vars: {
          "--gap": theme.spacings.m,
        },
      },
      l: {
        vars: {
          "--gap": theme.spacings.l,
        },
      },
    },
  },
  defaultVariants: {
    variant: "primary",
    applyPadding: true,
  },
});
